export type FscSide = 'carrier' | 'customer';
export type FscMode = 'fixed' | 'offset' | 'schedule';
export type Numeric = number | string | null;
export type ApiBoolean = boolean | string | null;
export type Load = 'ltl' | 'tl' | 'intermodal';

export const LOADS: Load[] = ['ltl', 'tl', 'intermodal'];

export interface EffectiveRow {
  effective_start?: string | null;
  effective_end?: string | null;
  active?: ApiBoolean;
  note?: string | null;
}

export interface FscDefault extends EffectiveRow {
  fuel_surcharge_default_uuid: string;
  effective_area: Numeric;
  rate_source: Numeric;
  default_ltl_rate: Numeric;
  default_tl_rate: Numeric;
  default_intermodal_rate: Numeric;
}

export type RateFields = Partial<
  Record<`${'cost' | 'chargeable'}_${Load}_${'rate' | 'offset_multiplier'}`, Numeric>
>;

export interface FscRate extends EffectiveRow, RateFields {
  fuel_surcharge_cost_rate_uuid?: string;
  fuel_surcharge_chargeable_rate_uuid?: string;
  carrier_contact_uuid?: string;
  customer_contact_uuid?: string;
  contact_organization?: string | null;
  uses_schedule?: ApiBoolean;
  offset_basis?: Numeric;
}

export interface FscSchedule extends EffectiveRow {
  fuel_surcharge_schedule_uuid: string;
  schedule_name: string;
  side: FscSide;
  carrier_contact_uuid?: string | null;
  customer_contact_uuid?: string | null;
  fuel_surcharge_cost_rate_uuid?: string | null;
  fuel_surcharge_chargeable_rate_uuid?: string | null;
  load_type: string;
  schedule_basis: string;
  effective_area: Numeric;
  currency_type: string | null;
  bracket_count?: number;
  brackets?: FscBracket[];
}

export interface FscBracket {
  fuel_surcharge_schedule_bracket_uuid: string;
  fuel_surcharge_schedule_uuid?: string;
  min_index: Numeric;
  max_index: Numeric;
  rate_amount: Numeric;
  rate_unit: string;
  sort_order: Numeric;
  active: ApiBoolean;
  note?: string | null;
}

export interface FscIndex extends EffectiveRow {
  fuel_surcharge_index_value_uuid: string;
  domain_uuid?: string | null;
  schedule_basis: string;
  effective_area: Numeric;
  index_value: Numeric;
  source_name?: string | null;
  source_url?: string | null;
}

export interface FscBasis {
  schedule_basis: string;
  label: string | null;
}

export type FormValue = string | number | boolean | null;
export type FscPayload = Record<string, FormValue>;

export function isActive(value: unknown): boolean {
  return value === true || ['true', 't', '1', 'yes', 'on'].includes(String(value).toLowerCase());
}

export function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

// The resolver accepts historical fractions (<= 1) and percentage points (> 1).
export function percentInput(value: unknown): number | null {
  const n = numberOrNull(value);
  return n === null ? null : Number((n <= 1 ? n * 100 : n).toFixed(8));
}

export function percentPayload(value: unknown): number | null {
  const n = numberOrNull(value);
  return n === null ? null : n <= 1 ? n / 100 : n;
}

export function rateId(row: FscRate, side: FscSide): string {
  return (
    (side === 'carrier'
      ? row.fuel_surcharge_cost_rate_uuid
      : row.fuel_surcharge_chargeable_rate_uuid) || ''
  );
}

export function rateMode(row: FscRate, side: FscSide): FscMode {
  if (isActive(row.uses_schedule)) return 'schedule';
  const prefix = side === 'carrier' ? 'cost' : 'chargeable';
  return LOADS.some(load => numberOrNull(row[`${prefix}_${load}_offset_multiplier`]) !== null)
    ? 'offset'
    : 'fixed';
}

export function contactPayload(raw: FscPayload, side: FscSide): FscPayload {
  const prefix = side === 'carrier' ? 'cost' : 'chargeable';
  const payload: FscPayload = {
    effective_start: raw['effective_start'],
    effective_end: raw['effective_end'] || null,
    active: raw['active'],
    note: raw['note'] || null,
    uses_schedule: raw['mode'] === 'schedule',
    offset_basis: raw['mode'] === 'offset' ? numberOrNull(raw['offset_basis']) : null
  };
  // Explicit nulls clear values from the previous mode on PUT.
  for (const load of LOADS) {
    payload[`${prefix}_${load}_rate`] = raw['mode'] === 'fixed' ? percentPayload(raw[load]) : null;
    payload[`${prefix}_${load}_offset_multiplier`] =
      raw['mode'] === 'offset' ? numberOrNull(raw[load]) : null;
  }
  return payload;
}

export function compatibleSchedules(
  rows: FscSchedule[],
  side: FscSide,
  contact: string,
  rate: string
): FscSchedule[] {
  return rows.filter(row => {
    const owner = side === 'carrier' ? row.carrier_contact_uuid : row.customer_contact_uuid;
    const linked =
      side === 'carrier'
        ? row.fuel_surcharge_cost_rate_uuid
        : row.fuel_surcharge_chargeable_rate_uuid;
    return row.side === side && (!owner || owner === contact) && (!linked || linked === rate);
  });
}

export function bracketError(raw: FscPayload, siblings: FscBracket[], editingId = ''): string {
  const min = numberOrNull(raw['min_index']);
  const max = numberOrNull(raw['max_index']);
  const amount = numberOrNull(raw['rate_amount']);
  if (min === null || amount === null) return 'Enter a minimum index and a rate amount.';
  if (amount < 0) return 'The rate amount cannot be negative.';
  if (max !== null && max < min) return 'The maximum index must be at least the minimum.';
  if (
    isActive(raw['active']) &&
    siblings.some(row => {
      if (!isActive(row.active) || row.fuel_surcharge_schedule_bracket_uuid === editingId)
        return false;
      return (
        min <= (numberOrNull(row.max_index) ?? Infinity) &&
        (max ?? Infinity) >= Number(row.min_index)
      );
    })
  )
    return 'This range overlaps an active bracket. Both ends are inclusive.';
  return '';
}
